import { useMeQuery } from '@shared/api';

/**
 * Discriminated union кодов агентов, к которым применим RBAC на фронте.
 * Сужает API: в проде в `agents[]` могут прийти и другие коды, но
 * read/write-гард в UI имеет смысл только для перечисленных — остальные
 * (admin-only роуты) защищены отдельно через require_admin на бэке.
 */
export type AgentCode =
  | 'statut'
  | 'dispatcher'
  | 'smeta'
  | 'econ'
  | 'pto'
  | 'mto'
  | 'meetings'
  | 'field'
  | 'progress'
  | 'ksp'
  | 'logist'
  | 'kadry'
  | 'geodesy'
  | 'ceo'
  | 'finance'
  /** ОСНОВА.Архивариус — вопросы по документам Диска (миграция 0322: у всех ролей). */
  | 'archivist';

/**
 * Возвращает can_read / can_write для текущего пользователя по конкретному
 * агенту. Источник истины — `/auth/me` (бэк формирует список после
 * проверки роли в `user_roles` + матрицу из миграции 0056).
 *
 * Если `/auth/me` ещё грузится или агент не упомянут в ответе — оба флага
 * возвращаются как false. Это безопасный дефолт: лучше дизейблить кнопку
 * до загрузки прав, чем разрешить write и получить 403 от бэка.
 */
export function useAgentPermission(agentCode: AgentCode) {
  const { data } = useMeQuery();
  const isAdmin = Boolean(data?.roles?.includes('admin'));
  const p = data?.agents.find((a) => a.agent_code === agentCode);
  if (agentCode === 'ceo') {
    const isDirector = Boolean(data?.roles?.includes('director'));
    const fallback = data?.agents.find((a) => a.agent_code === 'progress');
    return {
      can_read: Boolean(isAdmin || isDirector || p?.can_read || fallback?.can_read),
      can_write: Boolean(isAdmin || isDirector || p?.can_write || fallback?.can_write),
    };
  }
  if (agentCode === 'finance') {
    const roles = data?.roles ?? [];
    const canWriteFinance =
      isAdmin ||
      roles.includes('director') ||
      roles.includes('manager') ||
      roles.includes('financier');
    const pCeo = data?.agents.find((a) => a.agent_code === 'ceo');
    const pEcon = data?.agents.find((a) => a.agent_code === 'econ');
    return {
      can_read: Boolean(
        canWriteFinance || p?.can_read || pCeo?.can_read || pEcon?.can_read,
      ),
      can_write: Boolean(canWriteFinance),
    };
  }
  return {
    can_read: Boolean(isAdmin || p?.can_read),
    can_write: Boolean(isAdmin || p?.can_write),
  };
}

/**
 * Право помечать на удаление / восстанавливать сущности ПТО (проекты, документы
 * РД, ревизии, акты). Soft-delete с зачисткой через 30 дней доступен ролям
 * `engineer_plus` («Инженер+»), `admin` («Администратор») и `manager`
 * («Руководитель») — зеркалит бэкенд-гард `require_engineer_plus_or_admin`.
 * Дефолт (грузится /auth/me) — false.
 */
export function usePtoDeleteAccess(): boolean {
  const { data } = useMeQuery();
  const roles = data?.roles ?? [];
  return (
    roles.includes('engineer_plus') ||
    roles.includes('admin') ||
    roles.includes('manager') ||
    roles.includes('director')
  );
}

/**
 * Право помечать на удаление / восстанавливать совещания (ОСНОВА.Совещания).
 * Soft-delete с зачисткой через 30 дней доступен ролям `admin` («Администратор»),
 * `manager` («Руководитель») и `director` («Генеральный директор») — зеркалит бэкенд-гард `require_manager_or_admin`
 * на роутах `/meetings/{id}/delete|restore`. Дефолт (пока грузится /auth/me) — false.
 */
export function useMeetingDeleteAccess(): boolean {
  const roles = useMeQuery().data?.roles ?? [];
  return roles.includes('admin') || roles.includes('manager') || roles.includes('director');
}

/**
 * Право изменять счета и выписки, кредиты и лизинг, планы поступлений (запись через
 * `/agents/ceo/*`). Доступно ролям `admin`, `director`, `economist_plus` и `financier` —
 * зеркалит бэкенд-гард `check_ceo_access(write)` (`routes/ceo/mod.rs`). Право модуля
 * Финансиста сюда не подходит: там есть `manager`, но нет `economist_plus`, и кнопка
 * вела бы к отказу 403. Дефолт (пока грузится /auth/me) — false.
 */
export function useCeoWriteAccess(): boolean {
  const roles = useMeQuery().data?.roles ?? [];
  return (
    roles.includes('admin') ||
    roles.includes('director') ||
    roles.includes('economist_plus') ||
    roles.includes('financier')
  );
}
